import { checkValue } from '../../helper';

/* Get the token based on the theme color mode */
/* @param cpds - cpds object
    @param globalThemeColorMode - global theme color mode - set from the theme provider 
    @param moduleThemColorMode - module theme color mode - set from the module level
    @returns token */

export const themeToken = (cpds: [], globalThemeColorMode: string, moduleThemColorMode: string | undefined) => {
  // Get the theme color mode from the module level or global level ex: light or dark
  const themeColorMode = checkValue(moduleThemColorMode) ? moduleThemColorMode : globalThemeColorMode;
  const token = cpds[themeColorMode || 'light'];
  return { token, themeColorMode };
}

/**
 * 
 * @param cpdsComponentClass  Componet Class ex: cpds-button
 * @param themeTypographyClass | cpds-display-roman-book-01
 * @param extraClass  Extra class
 * @returns 
 */
export const themeStyleClass = (cpdsComponentClass, themeTypographyClass, extraClass) => {
  let themeClasses = cpdsComponentClass;
  if (checkValue(themeTypographyClass)) {
    themeClasses = `${themeTypographyClass} ${cpdsComponentClass}`;
  }

  if (checkValue(extraClass)) {
    themeClasses = `${themeClasses} ${extraClass}`;
  }

  return themeClasses;
}

export enum ThemeColorMode {
  Light = 'light',
  Dark = 'dark',
}

export type ThemeColorModeProps = {
  themeColorMode?: ThemeColorMode;
  themeTypography?: ThemeTypographyClass;
};

export const possibleClasses = [
  'cpds-display-roman-book-01',
  'cpds-display-roman-book-02',
  'cpds-display-roman-book-03',
  'cpds-display-roman-book-04',
  'cpds-display-roman-book-05',
  'cpds-display-roman-book-06',
  'cpds-display-italics-book-01',
  'cpds-display-italics-book-02',
  'cpds-display-italics-book-03',
  'cpds-display-italics-book-04',
  'cpds-display-italics-book-05',
  'cpds-display-italics-book-06',
  'cpds-heading-roman-book-07',
  'cpds-body-roman-book-07',
  'cpds-body-roman-book-08',
  'cpds-body-italics-book-07',
  'cpds-body-italics-book-08',
  'cpds-meta-roman-book-09',
  'cpds-meta-roman-book-10',
  'cpds-meta-roman-medium-book-09',
  'cpds-meta-roman-medium-book-10',
  'cpds-meta-italics-book-09',
  'cpds-meta-italics-book-10',
] as const;

export type ThemeTypographyClass = typeof possibleClasses[number];

export const convertObjectToCSS = (styles) => {
  let cssString = '';
  if (checkValue(styles)){
    for (const [selector, style] of Object.entries(styles)) {
        cssString += `${selector} { ${style} }`;
    }
    return cssString;
  } else{
    return cssString;
  }  
}

/**
 * Formats an HTML string by adding a specified class name to the specified HTML tag.
 *
 * @param {string} htmlString - The HTML string that needs to be formatted.
 * @param {string} className - The class name to be added to the specified tag.
 * @param {string} [italicTag="i"] - The HTML tag to be targeted (default is "i" for italic tags).
 *
 * @returns {string} - The formatted HTML string with the class name added to the specified tag.
 *
 * @example
 * // Example usage:
 * const html = 'normal text goes here <i>Some italic text</i> normal text goes here';
 * const formattedHtml = formatHtmlString(html, 'my-class');
 * // formattedHtml => 'normal text goes here <i class="my-class">Some italic text</i> normal text goes here '
 */
export const formatHtmlString = (
  htmlString: string,
  className: string,
  italicTag: string = "i"
): string => {
  const openingTagRegex = new RegExp(`<${italicTag}>`, 'g');
  const closingTagRegex = new RegExp(`</${italicTag}>`, 'g');

  const updatedHtml = htmlString.replace(openingTagRegex, `<${italicTag} class="${className}">`);

  return updatedHtml.replace(closingTagRegex, `</${italicTag}>`);
};

export const getCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return null; // Ensure browser context
  const cookieString = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return cookieString ? decodeURIComponent(cookieString.split('=')[1]) : null;
};
